import React, { useState, useEffect, useMemo } from 'react';
import { amDesignColors, amDesignTypography, amDesignComponents } from '../themes/amDesignSystem';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Chip,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Alert,
  CircularProgress,
  Divider,
  Grid,
  Card,
  CardContent,
  Autocomplete
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Search as SearchIcon,
  Category as CategoryIcon,
  AutoAwesome as AIIcon,
  CheckCircle as CheckIcon,
  Close as CloseIcon
} from '@mui/icons-material';

const CategoryManager = ({ 
  open, 
  onClose, 
  currentCategories = [], 
  onSave,
  businessData = {}
}) => {
  const [selectedCategories, setSelectedCategories] = useState(currentCategories);
  const [allCategories, setAllCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  // Fetch all categories on mount
  useEffect(() => {
    if (open) {
      fetchCategories();
      if (businessData?.business_name) {
        fetchAISuggestions();
      }
    }
  }, [open, businessData]);

  // Filter categories based on search term
  useEffect(() => {
    if (searchTerm) {
      const filtered = allCategories.filter(cat => 
        cat.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
        cat.key.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredCategories(filtered.slice(0, 50)); // Limit results for performance
    } else {
      setFilteredCategories([]);
    }
  }, [searchTerm, allCategories]);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/categories');
      const data = await response.json();
      if (data.success) {
        setAllCategories(data.categories);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
    setLoading(false);
  };

  const fetchAISuggestions = async () => {
    setLoadingSuggestions(true);
    try {
      const response = await fetch('/api/categories/suggest', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          businessName: businessData.business_name,
          businessDescription: businessData.business_description || '',
          currentCategories: selectedCategories.map(cat => cat.label),
          keywords: [] // Could be enhanced with actual keywords
        })
      });
      const data = await response.json();
      if (data.success) {
        setAiSuggestions(data.suggestions);
      }
    } catch (error) {
      console.error('Error fetching AI suggestions:', error);
    }
    setLoadingSuggestions(false);
  };

  const addCategory = (category) => {
    if (selectedCategories.length >= 10) {
      alert('Maximal 10 Kategorien erlaubt');
      return;
    }
    
    const exists = selectedCategories.some(cat => cat.key === category.key);
    if (!exists) {
      setSelectedCategories([...selectedCategories, category]);
    }
  };

  const removeCategory = (categoryKey) => {
    setSelectedCategories(selectedCategories.filter(cat => cat.key !== categoryKey));
  };

  const addAISuggestion = (suggestion) => {
    addCategory(suggestion);
  };

  const handleSave = () => {
    onSave(selectedCategories);
    onClose();
  };

  const availableCategories = useMemo(() => {
    return filteredCategories.filter(cat => 
      !selectedCategories.some(selected => selected.key === cat.key)
    );
  }, [filteredCategories, selectedCategories]);

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 10px 40px rgba(0,0,0,0.1)'
        }
      }}
    >
      <DialogTitle sx={{ 
        bgcolor: amDesignColors.paper, 
        borderBottom: '1px solid #e5e5e5',
        py: 3,
        px: 4
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ color: amDesignColors.primary }}><CategoryIcon /></Box>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
            Kategorien verwalten
          </Typography>
          <Chip 
            label={`${selectedCategories.length}/10`}
            size="small"
            sx={{ 
              bgcolor: '#e3f2fd', 
              color: amDesignColors.primary,
              fontWeight: 500,
              border: '1px solid amDesignColors.primary'
            }}
          />
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 4 }}>
        <Grid container spacing={4}>
          {/* Clean AI Suggestions */}
          <Grid item xs={12}>
            <Box sx={{ p: 4, bgcolor: '#f8f9ff', borderRadius: 2, border: '2px solid amDesignColors.primary' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Box sx={{ color: amDesignColors.primary }}><AIIcon /></Box>
                <Typography variant="h6" sx={{ color: amDesignColors.primary, fontWeight: 600 }}>
                  KI-Empfehlungen
                </Typography>
                {loadingSuggestions && <CircularProgress size={20} sx={{ color: amDesignColors.primary }} />}
              </Box>
                
              {aiSuggestions.length > 0 ? (
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  {aiSuggestions.map((suggestion, index) => (
                    <Chip
                      key={suggestion.key}
                      label={suggestion.label}
                      onClick={() => addAISuggestion(suggestion)}
                      clickable
                      sx={{
                        bgcolor: amDesignColors.primary,
                        color: amDesignColors.white,
                        fontWeight: 500,
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: amDesignColors.primary
                        }
                      }}
                    />
                  ))}
                </Box>
              ) : (
                <Box sx={{ p: 3, textAlign: 'center', bgcolor: '#f5f5f5', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ color: amDesignColors.text.secondary }}>
                    {loadingSuggestions ? 'KI analysiert Ihr Unternehmen...' : 'Keine KI-Empfehlungen verfügbar'}
                  </Typography>
                </Box>
              )}
            </Box>
          </Grid>

          {/* Selected Categories */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: 400 }}>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckIcon color="success" />
                Ausgewählte Kategorien ({selectedCategories.length}/10)
              </Typography>
              
              {selectedCategories.length === 0 ? (
                <Alert severity="info">
                  Keine Kategorien ausgewählt. Nutzen Sie die KI-Empfehlungen oder suchen Sie manuell.
                </Alert>
              ) : (
                <List sx={{ maxHeight: 300, overflow: 'auto' }}>
                  {selectedCategories.map((category, index) => (
                    <ListItem 
                      key={category.key}
                      sx={{ 
                        border: '1px solid amDesignColors.border',
                        borderRadius: 1,
                        mb: 1,
                        bgcolor: index === 0 ? 'rgba(76, 175, 80, 0.1)' : amDesignColors.white
                      }}
                    >
                      <ListItemText 
                        primary={category.label}
                        secondary={index === 0 ? 'Hauptkategorie' : `Zusatzkategorie ${index}`}
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          onClick={() => removeCategory(category.key)}
                          size="small"
                          color="error"
                        >
                          <RemoveIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </Paper>
          </Grid>

          {/* Search & Add Categories */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: 400 }}>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <SearchIcon />
                Kategorie hinzufügen
              </Typography>
              
              <Autocomplete
                options={allCategories}
                getOptionLabel={(option) => option.label}
                filterOptions={(options, { inputValue }) => {
                  return options.filter(option =>
                    option.label.toLowerCase().includes(inputValue.toLowerCase()) ||
                    option.key.toLowerCase().includes(inputValue.toLowerCase())
                  ).slice(0, 50);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Kategorie suchen..."
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: <SearchIcon sx={{ color: 'action.active', mr: 1 }} />
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box
                    component="li"
                    {...props}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      '&:hover': { bgcolor: 'action.hover' }
                    }}
                  >
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {option.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.key}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        addCategory(option);
                      }}
                      disabled={selectedCategories.some(cat => cat.key === option.key)}
                    >
                      <AddIcon />
                    </IconButton>
                  </Box>
                )}
                onChange={(event, value) => {
                  if (value) {
                    addCategory(value);
                  }
                }}
                value={null}
                sx={{ mb: 2 }}
              />

              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="caption">
                  <strong>Tipp:</strong> Die erste Kategorie wird als Hauptkategorie verwendet. 
                  Wählen Sie spezifische Kategorien für bessere lokale Auffindbarkeit.
                </Typography>
              </Alert>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ 
        p: 4, 
        bgcolor: amDesignColors.paper, 
        borderTop: '1px solid #e5e5e5',
        gap: 2,
        justifyContent: 'space-between'
      }}>
        <Button 
          onClick={onClose}
          sx={{
            color: amDesignColors.text.secondary,
            fontWeight: 500,
            px: 3,
            py: 1,
            textTransform: 'none',
            '&:hover': { bgcolor: '#f5f5f5' }
          }}
        >
          Abbrechen
        </Button>
        <Button 
          variant="contained" 
          onClick={handleSave}
          disabled={selectedCategories.length === 0}
          sx={{
            bgcolor: '#22c55e',
            color: amDesignColors.white,
            fontWeight: 600,
            px: 4,
            py: 1,
            textTransform: 'none',
            '&:hover': { bgcolor: '#16a34a' }
          }}
        >
          Kategorien speichern ({selectedCategories.length})
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CategoryManager;